import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SerializedError } from '@reduxjs/toolkit';
import { Stack } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AppErrorManager } from 'errorHelper';
import aiMusicGeneratorApi from 'services/api/aiMusicGenerator';
import { analytics, events } from 'services/analytics';
import useAnalytics from 'services/hooks/analytics';
import useUrlLocation from 'services/hooks/urlLocation';
import AudioInfoBox from './components/AudioInfoBox';
import InstructionsContainer from './components/InstructionsContainer';
import InstrumentSelector from './components/InstrumentSeletor/InstrumentSelector';
import {
  StyledActionButtonsContainer,
  StyledAudioInfoWrapper,
  StyledBackButton,
  StyledCancelButton,
  StyledDescription,
  StyledLoadingButton,
  StyledModal,
  StyledModalInner,
  StyledTitle,
} from './styled';
import MessagesContainer from './components/UploadMessage';
import ErrorMessagesContainer from './components/ErrorMessagesContainer';
import LoopFileSelector from './components/LoopFileSelector';
import useMediaProcessor from './hooks';

export interface UploadLoopData {
  id: string;
  file: File;
  instrument: string;
}

interface UserLoopModalProps {
  open: boolean;
  onClose?: () => void;
  onUpload: (data: UploadLoopData) => void;
}

const UserLoopModal = ({ open, onClose, onUpload }: UserLoopModalProps) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const location = useUrlLocation();

  const [
    upload,
    { isLoading: isUploadLoading, error: uploadError, reset: resetUploadData },
  ] = aiMusicGeneratorApi.useUploadUserLoopMutation();

  const [
    processMedia,
    {
      isLoading: isMediaProcessingLoading,
      error: mediaProcessingError,
      reset: resetAudioExtractionData,
    },
  ] = useMediaProcessor();

  const [userLoopFile, setUserLoopFile] = useState<File | null>(null);
  const [selectedInstrumentType, setSelectedInstrumentType] = useState<
    string | null
  >(null);
  const [validationError, setValidationError] =
    useState<SerializedError | null>(null);

  const handleAddFile = useCallback((file: File) => {
    setUserLoopFile(file);
    setValidationError(null);
  }, []);

  const handleAddFileError = useCallback(
    (file: File, error: SerializedError) => {
      setUserLoopFile(file);
      setValidationError(error);
      return analytics.track(
        events.audioUploadError(error?.message ?? '', file.type, location)
      );
    },
    [location]
  );

  const handleDeleteUserAudioFile = () => {
    setUserLoopFile(null);
    setSelectedInstrumentType(null);
    setValidationError(null);
    resetUploadData();
    resetAudioExtractionData();
  };

  const error = (uploadError ||
    validationError ||
    mediaProcessingError) as SerializedError | null;

  const isDisabled = !userLoopFile || !selectedInstrumentType || !!error;

  const isLoading = isUploadLoading || isMediaProcessingLoading;

  const handleUpload = useCallback(async () => {
    if (isDisabled) return;

    try {
      const audioFile = await processMedia(userLoopFile);
      const { id } = await upload({
        file: audioFile,
        instrument: selectedInstrumentType,
      }).unwrap();

      onUpload({
        id,
        file: userLoopFile,
        instrument: selectedInstrumentType,
      });

      track(events.audioUploadSuccess(location));

      onClose?.();
    } catch (uploadHandlerError) {
      AppErrorManager.sendError(uploadHandlerError as Error);
    }
  }, [
    isDisabled,
    processMedia,
    userLoopFile,
    upload,
    selectedInstrumentType,
    onUpload,
    track,
    location,
    onClose,
  ]);

  const submitButtonText = useMemo(() => {
    if (isLoading) {
      return t('aiMusicGenerator:userLoopUploadModal:uploadButton:uploading');
    }
    return t('aiMusicGenerator:userLoopUploadModal:uploadButton:upload');
  }, [t, isLoading]);

  return (
    <StyledModal open={open} onClose={onClose}>
      <StyledModalInner>
        <Stack>
          <StyledTitle variant="h3">
            {t('aiMusicGenerator:userLoopUploadModal:title')}
          </StyledTitle>
          <StyledDescription>
            {t('aiMusicGenerator:userLoopUploadModal:description')}
          </StyledDescription>
        </Stack>

        {userLoopFile ? (
          <Stack spacing={2}>
            <StyledBackButton
              onClick={handleDeleteUserAudioFile}
              startIcon={<ArrowBackIcon />}
            >
              {t('aiMusicGenerator:userLoopUploadModal:back')}
            </StyledBackButton>
            <Stack spacing={1}>
              <StyledAudioInfoWrapper isError={!!error}>
                <AudioInfoBox
                  file={userLoopFile}
                  onDelete={handleDeleteUserAudioFile}
                />
              </StyledAudioInfoWrapper>
              {error?.message && (
                <ErrorMessagesContainer message={error.message} />
              )}
            </Stack>
            <InstrumentSelector
              value={selectedInstrumentType}
              onChange={setSelectedInstrumentType}
              disabled={!!error}
            />
            {isLoading && <MessagesContainer />}
          </Stack>
        ) : (
          <LoopFileSelector
            onAddFile={handleAddFile}
            onError={handleAddFileError}
          />
        )}

        <InstructionsContainer />
        <StyledActionButtonsContainer>
          <StyledCancelButton variant="text" onClick={onClose}>
            {t('aiMusicGenerator:userLoopUploadModal:cancel')}
          </StyledCancelButton>
          <StyledLoadingButton
            variant="rounded"
            disabled={isDisabled}
            loading={isLoading}
            onClick={handleUpload}
          >
            {submitButtonText}
          </StyledLoadingButton>
        </StyledActionButtonsContainer>
      </StyledModalInner>
    </StyledModal>
  );
};

export default UserLoopModal;
